import { Typography, Box, Button, useTheme, alpha } from '@mui/material';
import Grid from 'components/Grid';
import Image from 'next/image';

import { useGetEventTokensActiveQuery } from 'store/rtk/events/service';
import { EventLogo } from 'components/User/EventTokens';
import { motion } from 'framer-motion';

import { getCaseImageUrl } from 'utils/outerImages';
import Link from 'next/link';
import { lowerCaseName } from 'utils/string';
import { EVENT_COLOR } from 'constants/event/details';

export const EventCase = ({
  caseName = '',
  image = '',
  name = '',
  price = 0,
  chance,
  boxProps
}) => {
  const theme = useTheme();
  const { data: activeToken } = useGetEventTokensActiveQuery();

  return (
    <Link passHref href={`/event/case/[name]`} as={`/event/case/${lowerCaseName(caseName)}`}>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        height={{ phxs: 240, tbxs: 290, tblg: 320, lplg: 340, lp: 300, dt: 330 }}
        minWidth={{ phxs: 'auto', tbxs: 220 }}
        gap={0.5}
        borderRadius={4}
        position='relative'
        component={motion.div}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        overflow='hidden'
        {...boxProps}
      >
        <Box width={1} height={1} position='relative'>
          {image ? (
            <Image
              priority
              unoptimized
              loading='eager'
              alt={caseName}
              src={getCaseImageUrl(image)}
              key={`next-image-event-case-${image}`}
              fill
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                display: 'block',
                borderRadius: 16
              }}
              sizes='99vw'
            />
          ) : null}
        </Box>

        <Box
          width={1}
          height={1}
          position='absolute'
          sx={{
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 50%, rgba(0, 0, 0, 0.4) 100%)'
          }}
        />

        {/* TOKEN */}
        {activeToken ? (
          <Box display='flex' position='absolute' top={8} right={8} zIndex={0}>
            <Box
              position='absolute'
              width={1}
              height={1}
              bgcolor={theme.palette.greyTransparency[600]}
              borderRadius={4}
              sx={{
                backdropFilter: 'blur(12px)'
              }}
              zIndex={-1}
            />
            <Box display='flex' gap={0.5} p={theme.spacing(0.5, 1, 0.5, 0.5)}>
              <EventLogo
                width={24}
                height={24}
                icon={activeToken?.icon}
                title={activeToken?.title}
              />
              <Typography variant='body1'>{chance}%</Typography>
            </Box>
          </Box>
        ) : null}

        <Box
          width={1}
          height={1}
          position='absolute'
          sx={{
            borderTop: `2px solid ${theme.palette.greyTransparency[300]}`,
            borderRadius: 4
          }}
        >
          {/* NAME & PRICE */}
          <Box position='absolute' right={0} left={0} bottom={0} p={2}>
            <Box
              display='flex'
              gap={1.5}
              flexDirection='column'
              justifyContent='center'
              alignItems='center'
            >
              <Typography variant='body1'>{name}</Typography>
              <Box
                display={'flex'}
                bgcolor={EVENT_COLOR}
                py={1}
                px={2}
                borderRadius={2}
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  backgroundColor: theme.palette.grey[600],
                  color: EVENT_COLOR
                }}
              >
                ${price?.toFixed(2)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

export const EventCasesGrid = ({ cases = [] }: { cases: any[] }) => {
  return (
    <Grid container spacing={2} justifyContent='center' zIndex={1}>
      {cases?.map((caseEn) => (
        <Grid
          item
          phxs={6}
          phlg={4}
          tbxs={5}
          tb={4}
          tblg={4}
          lpxs={3}
          lp={3}
          lplg={3}
          dtxs={2.5}
          dt={2}
          key={`event-case-${caseEn?.id}`}
        >
          <EventCase
            caseName={caseEn?.name}
            image={caseEn?.caseImg}
            name={caseEn?.name}
            price={caseEn?.price}
            chance={caseEn?.tokensWinRate}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const EventCases = ({ cases = [] }: { cases: any[] }) => {
  return (
    <Box
      component={Grid}
      // display='flex'
      // width={1}
      sx={{
        overflowX: 'auto',
        '::-webkit-scrollbar': {
          display: 'none'
        }
      }}
      container
      spacing={2}
      justifyContent={{ phxs: '', dt: 'center' }}
      zIndex={1}
      flexDirection='row'
      flexWrap='nowrap'
      // overflow='hidden'
      pb={2}
    >
      {cases?.map((caseEn) => (
        <Grid item phxs={'auto'} lp={12 / 5} dt={2} key={`event-case-${caseEn?.id}`}>
          <EventCase
            caseName={caseEn?.name}
            image={caseEn?.caseImg}
            name={caseEn?.name}
            price={caseEn?.price}
            chance={caseEn?.tokensWinRate}
            boxProps={{ minWidth: 220 }}
          />
        </Grid>
      ))}
    </Box>
  );
};

export default EventCases;
